/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { observer } from 'mobx-react';
import React from 'react';
import { Page } from 'src/core/components/Page';
import { ShimmerFactory } from 'src/core/components/ShimmerFactory';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { globalMessages } from 'src/core/utils/globalMessages';
import { useModels } from 'src/luis/modules/models/useModels';
import { EntityStore } from 'src/luis/types/stores/entities/EntityStore';
import { EntityDetails } from 'src/luis/verticals/entityDetails/EntityDetails';

/**
 * A page displaying the entity details for all entity types.
 */
export const EntityPage = observer((props: { entityId: string }) => {
    const { entityId } = props;
    const { entityStores, isLoaded, isLoading } = useModels();
    const { localize } = useLocalization();

    const entityStore: EntityStore = entityStores.find(e => e.id === entityId);

    // Redirect to 404 if the entity id isn't found
    if (!entityStore && isLoaded) {
        return null;
    }

    return (
        <Page pageName="EntityPage" size="container-fluid" title={isLoaded ? entityStore.name : localize(globalMessages.loading)}>
            {isLoading ? (
                <ShimmerFactory fullWidth includeHeaderShimmer shimmerCount={10} />
            ) : isLoaded ? (
                <EntityDetails entityStore={entityStore} />
            ) : null}
        </Page>
    );
});
